import React, { useEffect, useState } from 'react';
import axios from 'axios';
import RegistrationScreen from './Screens/RegistrationScreen';
import ManageTag from '../../Management/ManageTag';
import StarsScreen from './Screens/StarsScreen';
import AnimationScreen from './Screens/AnimationScreen';
import GeoProcessingScreen from './Screens/Geo/GeoProcessingScreen';
import FeedbackScreen from './Screens/FeedBackScreen';
import FeedbackScreenHold from './Screens/FeedBackScreenHold';
import NoExecutionsScreen from './Screens/NoExecutionsScreen';
import DigitalContentPdfScreen from './Screens/DigitalContentPdfScreen';
import GeoRegisterScreen from './Screens/Geo/GeoRegisterScreen';
import GeoPositionScreen from './Screens/Geo/GeoPositionScreen';
import GeoVerifyScreen from './Screens/Geo/GeoVerifyScreen';
import CodeScannerScreen from './Screens/CodeScannerScreen';
import { useAppContext } from '../../Store/Store';
import CodeScannerPistolScreen from './Screens/CodeScannerPistolScreen';
import RegistrationScannerScreen from './Screens/RegistrationScannerScreen';
import { buildScreenSequence } from './screenSequence';

export default function Body() {
  const {
    token,
    product,
    event,
    appState,
    style,
    backendBaseUrl,
    userEmail,
    setUserEmail,
    setCurrentScreenKey,
  } = useAppContext();
  const [screens, setScreens] = useState<string[]>([]);

  const checkResetScreen = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetScreen = urlParams.get('reset_screen');
    if (resetScreen === 'true') {
      urlParams.delete('reset_screen');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);

      sessionStorage.removeItem('currentScreenIndex');
      return true;
    }
    return false;
  };

  const [currentScreenIndex, setCurrentScreenIndex] = useState(() => {
    const isReset = checkResetScreen();
    if (isReset) {
      return 0;
    }
    const savedIndex = sessionStorage.getItem('currentScreenIndex');
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });

  const [loading, setLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const noRemainingExecutions =
    product?.id != -1 && product?.remaining_executions <= 0;
  const [shouldSkipRegistration, setShouldSkipRegistration] = useState(false);

  // Check if we should show ManageGeo screen
  const checkForGeoManagement = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlProduct = urlParams.get('product');

    if (!urlProduct || !product) return false;

    // Extract base product code (remove /digital-content-pdf if present)
    const baseProductCode = urlProduct.split('/')[0];

    if (baseProductCode === product.code && product.type === 'geo') {
      // Only show geo management if we're not viewing digital content
      return !urlProduct.includes('/digital-content-pdf');
    }
    return false;
  };

  // Check if we should show Feedback screen
  const checkForFeedbackMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlProduct = urlParams.get('product');
    const feedbackParam = urlParams.get('feedback');

    if (
      urlProduct &&
      product &&
      product.code === urlProduct &&
      feedbackParam === 'true'
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (appState !== 'initial') return;
    if (!product || (!token && !product)) return;

    const fetchScreens = async () => {
      try {
        const response = await axios.get(`${backendBaseUrl}/event-screens/`, {
          params: { event_id: event.id },
        });
        const fetchedScreens = response.data.screens as string[];
        setScreens(buildScreenSequence(fetchedScreens, product));
      } catch (error) {
        console.error('Failed to fetch screens:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchScreens();
  }, [event, token, product, appState, backendBaseUrl]);

  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlProduct = urlParams.get('product');

      if (urlProduct && urlProduct.includes('/digital-content-pdf')) {
        const productCode = urlProduct.split('/')[0];
        if (product && product.code === productCode) {
          setScreens(['digital-content-pdf']);
          setCurrentScreenIndex(0);
          sessionStorage.setItem('currentScreenIndex', '0');
        }
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [product]);

  useEffect(() => {
    sessionStorage.setItem('currentScreenIndex', currentScreenIndex.toString());
  }, [currentScreenIndex]);

  useEffect(() => {
    if (
      currentScreenIndex === screens.indexOf('registration') &&
      event?.remember_registration &&
      userEmail
    ) {
      setShouldSkipRegistration(true);
    }
  }, [currentScreenIndex, screens, event?.remember_registration, userEmail]);

  useEffect(() => {
    if (shouldSkipRegistration) {
      nextScreen();
      setShouldSkipRegistration(false);
    }
  }, [shouldSkipRegistration]);

  // Add effect to skip geo screens if position is already set
  useEffect(() => {
    const currentKey = screens[currentScreenIndex];
    const nextKey = screens[currentScreenIndex + 1];

    if (
      product &&
      product.geo_attributes?.position &&
      (style?.geoAttributes?.geo_verify_range ?? null) !== null
    ) {
      if (
        currentKey === 'geo-register-screen' ||
        currentKey === 'geo-position-screen'
      ) {
        nextScreen();
      }
      // Also skip processing screen if it's between geo-position-screen and geo-verify-screen
      else if (
        currentKey === 'geo-processing' &&
        nextKey === 'geo-verify-screen'
      ) {
        nextScreen();
      }
    }
  }, [currentScreenIndex, screens, product]);

  const nextScreen = () => {
    setCurrentScreenIndex(prev => {
      const newIndex = screens.length ? (prev + 1) % screens.length : 0;
      if (newIndex === 0 && !event?.remember_registration) {
        setUserEmail('');
        sessionStorage.removeItem('userEmail');
      }
      sessionStorage.setItem('currentScreenIndex', newIndex.toString());
      return newIndex;
    });
  };

  const goBack = () => {
    setCurrentScreenIndex(prev => {
      const newIndex = prev > 0 ? prev - 1 : 0;
      sessionStorage.setItem('currentScreenIndex', newIndex.toString());
      return newIndex;
    });
  };

  const resetToScreen = (screenKey: string) => {
    const screenIndex = screens.indexOf(screenKey);
    if (screenIndex !== -1) {
      setCurrentScreenIndex(screenIndex);
      sessionStorage.setItem('currentScreenIndex', screenIndex.toString());
    } else {
      // If screen not found, default to feedback screen
      const feedbackIndex = screens.indexOf('feedback');
      if (feedbackIndex !== -1) {
        setCurrentScreenIndex(feedbackIndex);
        sessionStorage.setItem('currentScreenIndex', feedbackIndex.toString());
      }
    }
  };

  const renderCurrentScreen = () => {
    if (noRemainingExecutions) {
      return <NoExecutionsScreen />;
    }
    if (screens.length === 0) {
      return <FeedbackScreen onNext={nextScreen} deferSave={false} />;
    }
    if (currentScreenIndex >= screens.length) {
      return <div>All screens complete!</div>;
    }
    const currentKey = screens[currentScreenIndex];
    const nextKey = screens[currentScreenIndex + 1];

    switch (currentKey) {
      case 'registration':
        if (event?.remember_registration && userEmail) {
          return <div style={{ display: 'none' }}></div>;
        }
        return <RegistrationScreen onNext={nextScreen} />;
      case 'feedback':
        return (
          <FeedbackScreen
            onNext={nextScreen}
            deferSave={nextKey === 'stars'}
            setFeedbackData={nextKey === 'stars' ? setFeedbackData : undefined}
          />
        );
      case 'feedback-hold':
        return (
          <FeedbackScreenHold
            onNext={nextScreen}
            deferSave={nextKey === 'stars'}
            setFeedbackData={nextKey === 'stars' ? setFeedbackData : undefined}
          />
        );
      case 'stars':
        return (
          <StarsScreen
            onNext={nextScreen}
            feedbackData={feedbackData}
            setFeedbackData={setFeedbackData}
            goBack={goBack}
          />
        );
      case 'animation':
        return <AnimationScreen onNext={nextScreen} />;
      case 'digital-content-pdf':
        return (
          <DigitalContentPdfScreen
            onNext={nextScreen}
            resetToScreen={resetToScreen}
          />
        );
      case 'geo-register-screen':
        return <GeoRegisterScreen onNext={nextScreen} />;
      case 'geo-position-screen':
        return <GeoPositionScreen onNext={nextScreen} />;
      case 'geo-verify-screen':
        return <GeoVerifyScreen onNext={nextScreen} />;
      case 'geo-processing':
        return <GeoProcessingScreen onNext={nextScreen} />;
      case 'codescanner':
        return <CodeScannerScreen onNext={nextScreen} />;
      case 'codescannerpistol':
        return <CodeScannerPistolScreen onNext={nextScreen} />;
      case 'registrationscanner':
        return <RegistrationScannerScreen onNext={nextScreen} />;
      default:
        return <div>Screen "{currentKey}" not implemented.</div>;
    }
  };

  React.useEffect(() => {
    const routeMap: Record<string, string> = {
      registration: '/registration',
      feedback: '/feedback',
      'feedback-hold': '/feedback-hold',
      stars: '/stars',
      animation: '/animation',
      'digital-content-pdf': '/digital-content-pdf',
      'geo-register-screen': '/geo-register-screen',
      'geo-position-screen': '/geo-position-screen',
      'geo-verify-screen': '/geo-verify-screen',
      'geo-processing': '/geo-processing',
      codescanner: '/codescanner',
      codescannerpistol: '/codescannerpistol',
      registrationscanner: '/registrationscanner',
    };
    const currentKey = screens[currentScreenIndex];
    const desiredPath = routeMap[currentKey] || '/';
    const qs = window.location.search || '';
    if (desiredPath && window.location.pathname !== desiredPath) {
      window.history.replaceState({}, '', `${desiredPath}${qs}`);
    }
  }, [screens, currentScreenIndex]);

  // Come in ScreenRouter: l'Header sta fuori dal body e ha bisogno dello screen
  // corrente per capire se mostrare o no la domanda del prodotto. Qui serve una
  // via separata dall'URL, perche' il sync sopra usa replaceState e non passa da
  // react-router: la location del router resterebbe ferma su '/'.
  useEffect(() => {
    setCurrentScreenKey(
      screens.length > 0 ? (screens[currentScreenIndex] ?? null) : null
    );
    return () => setCurrentScreenKey(null);
  }, [screens, currentScreenIndex, setCurrentScreenKey]);

  if (appState === 'no-token') {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        Invalid token provided.
      </div>
    );
  }
  if (appState === 'error') {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        Error occurred. Please try again later.
      </div>
    );
  }
  if (appState === 'managetag') {
    return <ManageTag />;
  }

  if (loading) {
    return <div style={{ flex: 1, minHeight: 0, width: '100%' }}></div>;
  }

  const isOverrideFixedHeight =
    screens[currentScreenIndex] === 'registration' ||
    screens[currentScreenIndex] === 'digital-content-pdf';
  const containerClass = isOverrideFixedHeight
    ? 'registration-main-product'
    : 'main-product';

  return (
    <div
      className={containerClass}
      style={{ width: '100%', flex: 1, minHeight: 0, boxSizing: 'border-box' }}
    >
      {renderCurrentScreen()}
    </div>
  );
}
